import { CheckIcon } from 'lucide-react';
import { Select as SelectPrimitive } from 'radix-ui';
import { type ComponentPropsWithoutRef, type FC } from 'react';
import { portaledSurfaceVariants, type PortaledSurfaceVariant } from '../lib/portaledSurface';
import { cn } from '../lib/utils';
import {
  SelectScrollDownButton,
  SelectScrollUpButton,
  Select as ShadcnSelect,
  SelectContent as ShadcnSelectContent,
  SelectItem as ShadcnSelectItem,
  SelectTrigger as ShadcnSelectTrigger,
} from './ui/select';

/** Radix reserves `""` to clear the selection. Items that mean none use this instead. */
const SELECT_NONE = '\u0000';
const toRadixValue = <T extends string | undefined>(value: T) =>
  (value === '' ? SELECT_NONE : value) as T;
const fromRadixValue = (value: string) => (value === SELECT_NONE ? '' : value);

type SelectTriggerProps = ComponentPropsWithoutRef<typeof ShadcnSelectTrigger>;

export const SelectTrigger: FC<SelectTriggerProps> = (props) => {
  return (
    <ShadcnSelectTrigger
      {...props}
      className={cn(
        `overflow-hidden text-ellipsis body-md-strong [&_svg:not([class*='text-'])]:text-foreground-primary [&_svg:not([class*='text-'])]:opacity-100`,
        // Override ui/select `dark:bg-input/30` + hover `/50` → Figma `background/surface`.
        'dark:bg-background-surface dark:hover:bg-background-surface',
        props.className,
      )}
    />
  );
};

type SelectProps = ComponentPropsWithoutRef<typeof ShadcnSelect>;

export const Select: FC<SelectProps> = ({ value, defaultValue, onValueChange, ...props }) => {
  return (
    <ShadcnSelect
      value={toRadixValue(value)}
      defaultValue={toRadixValue(defaultValue)}
      onValueChange={onValueChange ? (next) => onValueChange(fromRadixValue(next)) : undefined}
      {...props}
    />
  );
};

type SelectItemProps = ComponentPropsWithoutRef<typeof ShadcnSelectItem>;

export const SelectItem: FC<SelectItemProps> = ({ value, ...props }) => {
  return <ShadcnSelectItem value={toRadixValue(value)} {...props} />;
};

type SelectContentProps = ComponentPropsWithoutRef<typeof ShadcnSelectContent> & {
  container?: Element | null;
  /** Light (default) or dark surface. Pass `"dark"` for a select opened from inside a `DarkThemeScope` — it portals to `document.body` and so escapes the scope's own `.dark`. */
  variant?: PortaledSurfaceVariant;
};

export const SelectContent: FC<SelectContentProps> = ({
  container,
  variant = 'light',
  className,
  children,
  position = 'item-aligned',
  align = 'center',
  ...props
}) => {
  const contentClassName = cn('tw', portaledSurfaceVariants({ variant }), className);

  if (container) {
    return (
      <SelectPrimitive.Portal container={container}>
        <SelectPrimitive.Content
          {...props}
          data-slot="select-content"
          className={cn(
            'max-h-(--radix-select-content-available-height) origin-(--radix-select-content-transform-origin) relative z-50 min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            position === 'popper' &&
              'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
            contentClassName,
          )}
          position={position}
          align={align}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            className={cn(
              'p-1',
              position === 'popper' &&
                'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1',
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    );
  }

  return (
    <ShadcnSelectContent {...props} position={position} align={align} className={contentClassName}>
      {children}
    </ShadcnSelectContent>
  );
};

type SelectMenuItemProps = Omit<
  ComponentPropsWithoutRef<typeof SelectPrimitive.Item>,
  'children'
> & {
  label: string;
  description?: string;
  checkbox?: boolean;
};

/**
 * Visual-only checkbox indicator that reflects the parent SelectItem's
 * selected state via the `data-[state=checked]` attribute on the Item.
 */
const SelectCheckbox: FC = () => (
  <span
    aria-hidden
    className={cn(
      'shadow-xs flex size-4 shrink-0 items-center justify-center rounded border border-input transition-shadow',
      'group-data-[state=checked]:border-primary group-data-[state=checked]:bg-primary group-data-[state=checked]:text-primary-foreground',
    )}
  >
    <CheckIcon className="size-3.5 opacity-0 group-data-[state=checked]:opacity-100" />
  </span>
);

export const SelectMenuItem: FC<SelectMenuItemProps> = ({
  className,
  label,
  description,
  checkbox,
  value,
  ...props
}) => {
  return (
    <SelectPrimitive.Item
      data-slot="select-menu-item"
      className={cn(
        'outline-hidden group relative flex w-full cursor-default select-none items-center gap-2 rounded p-2 text-sm focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        !checkbox && 'pr-8',
        className,
      )}
      {...props}
      value={toRadixValue(value)}
    >
      {checkbox && <SelectCheckbox />}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <SelectPrimitive.ItemText>
          <span className="text-foreground-primary label-md">{label}</span>
        </SelectPrimitive.ItemText>
        {description && <span className="text-foreground-secondary caption">{description}</span>}
      </div>
      {!checkbox && (
        <span className="absolute right-2 flex size-3.5 items-center justify-center">
          <SelectPrimitive.ItemIndicator>
            <CheckIcon className="size-4" />
          </SelectPrimitive.ItemIndicator>
        </span>
      )}
    </SelectPrimitive.Item>
  );
};

export { SelectGroup, SelectLabel, SelectSeparator, SelectValue } from './ui/select';
export { SelectScrollDownButton, SelectScrollUpButton };

export type { PortaledSurfaceVariant as SelectSurfaceVariant };
